import type { Category, PrintArea, PrintMethod, ProductColor, ProductImage } from '@/features/gfProduct/types';
import type { SaleStatus, Spec } from '@/types';
import type { GfProductSize, GfSizeTableData } from '@/types';

export const PRODUCT_NAME = 'ジャンプLAB';

export const BREAKPOINT = {
  SP: 800,
};

// デバイスタイプ
export const DEVICE_TYPE = {
  PC: 'pc',
  SP: 'sp',
} as const;

export type DeviceType = (typeof DEVICE_TYPE)[keyof typeof DEVICE_TYPE] | '';

export const PRODUCT_COLORS = ['Skyblue', 'Green', 'Orange', 'Purple', 'Turquoise', 'Yellow'] as const;

export type ProductColorType = (typeof PRODUCT_COLORS)[number];

// 販売ステータスの定数
export const SALE_STATUS = {
  onSale: 'on_sale',
  discontinued: 'discontinued',
  comingSoon: 'coming_soon',
} as const;

// 販売終了時の表示メッセージ
export const DISCONTINUED_MESSAGE = 'こちらのアイテムは取り扱いを終了いたしました';

// 商品詳細の型定義
export type ProductDetail = {
  id: number;
  name: string;
  description: string;
  basePrice: number;
  taxExcludedPrice: number;
  model3dPath: string;
  texture3dPath: string;
  sizeTable: GfSizeTableData;
  displayOrder: number;
  status: string;
  createdAt: string;
  updatedAt: string;
  categories: Category[];
  printMethods: PrintMethod[];
  printAreas: PrintArea[];
  colors: ProductColor[];
  sizes: GfProductSize[];
  specs: Spec[];
  images: ProductImage[];
  saleStatus: SaleStatus;
};

// APIのレスポンスのcode
export const API_RESPONSE_CODE = {
  success: { value: 0 },
  error: { value: 1 },
  smsRequired: { value: 2 },
  banUser: { value: 3 },
  httpUnprocessableEntity: { value: 422 },
};

// 決済の種別
export const ITEM_TYPE = {
  coin: 'coin',
  oemSubscription: 'subscription',
  artistSubscription: 'artist_subscription',
};

// 通知パーミッション
export const NOTIFICATION_PERMISSION = {
  1: { text: '新着投稿' }, // プッシュ通知
  2: { text: '新着記事' }, // プッシュ通知
  4: { text: '新着メッセージ' }, // プッシュ通知
  8: { text: '新着投稿' }, // メール
  32: { text: '新着記事' }, // メール
  128: { text: '新着メッセージ' }, // メール
};

// メルマガ通知パーミッション
export const NEWSLETTER_PERMISSION = {
  1: { text: 'メルマガ' }, // プッシュ通知
  2: { text: 'メルマガ' }, // メール
};

// 配送ステータス
export const DELIVERY_STATUS = {
  unprocessed: {
    text: '準備中',
    value: 'unprocessed',
    color: 'var(--color-black)',
  },
  preparing: {
    text: '準備中',
    value: 'preparing',
    color: 'var(--color-black)',
  },
  inprocess: {
    text: '準備中',
    value: 'inprocess',
    color: 'var(--color-black)',
  },
  shipped: {
    text: '発送済',
    value: 'shipped',
    color: 'var(--color-black)',
  },
  complete: {
    text: 'お届け済',
    value: 'complete',
    color: 'var(--color-warmgray)',
  },
  no_address: {
    text: '住所未登録',
    value: 'no_address',
    color: '',
  },
  return: { text: '返品', value: 'return', color: 'var(--color-black)' },
};

// お支払い方法（APIの payment_method に渡す値）
export const PAYMENT_METHOD = {
  credit: 'credit',
  paypay: 'paypay',
} as const;

export type PaymentMethodType = (typeof PAYMENT_METHOD)[keyof typeof PAYMENT_METHOD];

// 前回選択決済手段の cookie（購入確認画面のデフォルト表示用）
export const LAST_SELECTED_PAYMENT_METHOD_COOKIE_NAME = 'last-selected-payment-method';

// 購入プラットフォーム
export const PLATFORM = {
  ios: { text: 'iOS', value: 'ios' },
  android: { text: 'Android', value: 'android' },
  web: { text: 'Web', value: 'web' },
};

// SNS種別
export const SNS_TYPE = {
  x: { value: 'x' }, //旧ツイッター
  instagram: { value: 'instagram' },
  tiktok: { value: 'tiktok' },
  youtube: { value: 'youtube' },
};

export const CANVAS_SCALE = {
  tShirt: [
    { partName: '前面', pc: { xScale: 0.075, yScale: 0.075 }, sp: { xScale: 0.8, yScale: 0.8 } },
    { partName: '背面', pc: { xScale: 0.075, yScale: 0.075 }, sp: { xScale: 0.8, yScale: 0.8 } },
    { partName: '左袖', pc: { xScale: 0.12, yScale: 0.12 }, sp: { xScale: 0.8, yScale: 0.8 } },
    { partName: '右袖', pc: { xScale: 0.12, yScale: 0.12 }, sp: { xScale: 0.8, yScale: 0.8 } },
  ],
};

// 発送予定日文言
export const SHIPPING_DATE_TEXT = '発送目安：ご注文から10日〜15日';

// βテストログイン時のcookieキー
// next-authで使用するjwtトークンとは別
export const BETA_AUTH_COOKIE_NAME = 'beta-auth-token';
export const TEASER_HASH_COOKIE_NAME = 'teaser-hash';

// 規約同意フラグのcookieキー
export const USER_TERMS_ACCEPTED_COOKIE_NAME = 'user-terms-accepted';

// 購入前注意事項の表示済みフラグのcookieキー
export const DESIGN_ANNOTATION_COOKIE_NAME = 'showDesignAnnotation';

// 素材カテゴリ
export const MaterialCategory = {
  frame: { value: 'frame', text: 'コマ' },
  logo: { value: 'logo', text: 'ロゴ' },
  text: { value: 'text', text: 'テキスト' },
  character: { value: 'character', text: 'キャラクター' },
} as const;

export type MaterialCategoryType = keyof typeof MaterialCategory;

// 素材の初期配置時の基準サイズ比率
// 表示キャンバスに対して49.6%の大きさ（2つ横並びで重ならないようにするため）
export const DEFAULT_MATERIAL_SIZE_RATIO = 0.496;

// 素材一覧のページネーション用のリミット
export const MATERIAL_PAGE_LIMIT = 100;

// 人気のコマ一覧のリミット
export const MATERIAL_POPULAR_PAGE_LIMIT = 6;

//サービス公式SNS
export const SERVICE_OFFICIAL_SNS_URL_X = 'https://x.com/jumplab_info';
export const SERVICE_OFFICIAL_SNS_URL_INSTAGRAM = 'https://www.instagram.com/jumplab_info/?hl=ja';

// ティーザーLP用YouTube動画ID
export const TEASER_YOUTUBE_VIDEO_ID = 'lG4Z3JTk9nM';

//ジャンプLABの固定ユーザ名
export const JUMPLAB_FIXED_USER_NAME = 'ジャンプLABユーザ';

// HTTPステータスコードとメッセージ
export const ERROR_STATUS = {
  unauthorized: {
    code: 401,
    message: `再度ログインしてください。<br>繰り返しても改善しない場合は<br><a href="/contact">お問い合わせページ</a>にてお問い合わせください。`,
  },
};

// コラボなどで画像が切り替わるときの基準日時
export const SWITCH_DATE = '2025-12-19 12:00:00';

// 年末年始休業のお知らせ表示終了日時
export const YEAR_END_NOTICE_END_DATE = '2026-01-05 00:00:00';

// カートマージ完了フラグ（sessionStorageキー）
// ログイン/会員登録時にローカルカートをマージした後、layout再実行でヘッダーのcartCountを最新化するために使用
export const CART_MERGED_FLAG = 'cartMergedNeedsRefresh';
